import React, {useMemo, useState} from 'react';
import {Box, Paper, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  Drill,
  DrillDetailRecord,
  TeamSessionSessionEvent,
  TeamSession,
} from '../../interfaces';
import DrillDetailsDataGrid from '../shared/DrillDetailsDataGrid';
import {getTime} from '../../services/dateService';
import SessionVideoPlayer, {
  SessionVideoPlayerItem,
} from '../shared/SessionVideoPlayer';

interface Props {
  teamSession: TeamSession;
  teamSessionDrillEvents: TeamSessionSessionEvent[];
  preferredUnits?: 'imperial' | 'metric';
}

const DrillList: React.FC<Props> = ({
  teamSessionDrillEvents,
  teamSession,
  preferredUnits,
}) => {
  const {t} = useTranslation();
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const data = useMemo<
    {
      drill?: Drill;
      drillDetail: DrillDetailRecord;
      teamSession?: TeamSession;
      teamSessionSessionEvent: TeamSessionSessionEvent;
    }[]
  >(
    () =>
      (teamSessionDrillEvents ?? []).flatMap(tsse =>
        (tsse.drillDetails ?? []).map(drillDetail => ({
          drill: tsse.drill,
          drillDetail,
          teamSession,
          teamSessionSessionEvent: tsse,
        })),
      ),
    [teamSession, teamSessionDrillEvents],
  );

  const videoItems = useMemo<SessionVideoPlayerItem[]>(() => {
    const seen = new Set<string>();
    return data
      .filter(d => !!d.drillDetail.videoUrl)
      .filter(d => {
        const url = d.drillDetail.videoUrl!;
        if (seen.has(url)) return false;
        seen.add(url);
        return true;
      })
      .sort((a, b) => {
        const aTime = a.teamSessionSessionEvent?.startTime;
        const bTime = b.teamSessionSessionEvent?.startTime;
        if (aTime && bTime) {
          return new Date(aTime).getTime() - new Date(bTime).getTime();
        }
        return a.drillDetail.id - b.drillDetail.id;
      })
      .map(d => {
        const tsseStart = d.teamSessionSessionEvent?.startTime;
        const sessionStart = d.teamSession?.startTime;
        let sessionTimeLabel: string | null = null;
        if (tsseStart && sessionStart) {
          const offsetSeconds = Math.max(
            0,
            (new Date(tsseStart).getTime() - new Date(sessionStart).getTime()) /
              1000,
          );
          sessionTimeLabel = getTime(offsetSeconds);
        }
        return {
          id: d.drillDetail.id,
          videoUrl: d.drillDetail.videoUrl,
          thumbnailUrl: d.drillDetail.thumbnailUrl,
          drillName: d.drill?.name,
          sessionTimeLabel,
        };
      });
  }, [data]);

  return (
    <Box sx={{mt: 5}}>
      <DrillDetailsDataGrid
        title={t('pages.drills')}
        data={data}
        page="teamSession"
        preferredUnits={preferredUnits}
        sx={{mb: 3}}
        onVideoSelect={setSelectedVideoUrl}
      />
      {videoItems.length > 0 && (
        <Paper sx={{p: 2, mb: 3}}>
          <Typography variant="h3" sx={{mb: 2}}>
            {t('library.drillVideos')}
          </Typography>
          <SessionVideoPlayer
            videoItems={videoItems}
            selectedVideoUrl={selectedVideoUrl}
            renderCard={({item, active, onClick}) => {
              const video = item as SessionVideoPlayerItem & {
                thumbnailUrl?: string;
                drillName?: string;
                sessionTimeLabel?: string | null;
              };
              return (
                <Box
                  onClick={onClick}
                  sx={{
                    width: 210,
                    minWidth: 210,
                    mx: 1,
                    borderRadius: 1,
                    border: theme =>
                      `2px solid ${
                        active
                          ? theme.palette.primary.main
                          : theme.palette.divider
                      }`,
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}>
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      style={{
                        width: '100%',
                        height: 100,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.800',
                      }}>
                      <Typography variant="caption">
                        {t('general.video')}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{p: 1}}>
                    {video.drillName && (
                      <Typography
                        variant="body1"
                        sx={{display: 'block', fontWeight: 700}}>
                        {video.drillName}
                      </Typography>
                    )}
                    {video.sessionTimeLabel && (
                      <Typography variant="caption" color="text.secondary">
                        {video.sessionTimeLabel}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default DrillList;
